import { Controller } from '@hotwired/stimulus'
import { stimulus } from '../application'
import BulkActionsController from './bulk-actions'

type RelationID = string
type RelationCount = number
type RelationType = string

/**
 * This data is a map of relation types that exist for a given entity
 * along with a list of existing relation IDs for each type:
 * @example
 * ```json
 * {
 *   "authors": ["author-uuid-1", "author-uuid-2"],
 *   "tags": ["tag-uuid-1"],
 *   "comments": []
 * }
 * ```
 *
 * This data should exist in the `data-entity-relations` attribute of
 * each checkbox that exists within the `BulkActionsController` UI:
 * @example
 * ```html
 * <label>
 *   <input
 *     type="checkbox"
 *     data-bulk-actions-target="checkbox"
 *     data-entity-relations="{
 *       'authors': ['author-uuid-1', 'author-uuid-2'],
 *       'tags': ['tag-uuid-1'],
 *       'comments': []
 *     }"
 *   />
 *   <span>Blog Post #1</span>
 * </label>
 * ```
 */
type EntityRelations = Record<RelationType, RelationID[]>

/**
 * @example
 * ```json
 * {
 *   "author-uuid-1": 3,
 *   "author-uuid-2": 1,
 *   "author-uuid-3": 12,
 * }
 * ```
 */
type RelationCounts = Record<RelationID, RelationCount>

/**
 * This controller works in tandem with the `BulkActionsController` to batch
 * the addition and removal of the specified type of relation to one or more
 * selected entities.
 *
 * The list of entities is set up to use `BulkActionsController`, so each entity
 * will have a checkbox with a `value` equal to the entity's ID. Whenever any of
 * these checkboxes are checked, the `BulkActionsController` will dispatch an
 * event containing an array of the checked entity IDs.
 *
 * This controller will respond by allowing the user to modify a list of
 * relation checkboxes for the checked entities. The state of the relation
 * checkboxes change anytime the user modifies an entity checkbox.
 *
 * For instance, consider a list of Blog Post entities and a list of Author
 * relations. If all of the Blog Posts that the user checked were written by
 * the same Author, the Author's relation checkbox would render as checked.
 * If _some but not all_ of the checked Blog Post entities were written by
 * the same Author, each Author's relation checkbox would appear as
 * indeterminate ("half checked") to communicate a partial match.
 *
 * As the user checks and unchecks relation checkboxes, this controller adds or
 * removes hidden inputs specifying existing relations to remove and new
 * relations to create.
 */
export default class EntityRelationsController extends Controller<HTMLElement> {
  declare bulkActionsController: BulkActionsController
  declare entityTypeValue: string
  declare relationCounts: RelationCounts
  declare relationTypeValue: RelationType
  declare readonly groupIndicatorTargets: HTMLElement[]
  declare readonly hiddenInputTargets: HTMLInputElement[]
  declare readonly relationCheckboxTargets: HTMLInputElement[]

  static targets = [
    // (Optional) If relation checkboxes are organized into groups, these are
    // little dots that appear at the top level of the group to indicate
    // that the user has modified one or more relation checkboxes within it
    'groupIndicator',

    // A hidden <input> that tells the backend which relations the user is
    // adding to or removing from the checked entities
    'hiddenInput',

    // The checkboxes for the provided list of available relations
    'relationCheckbox',
  ]

  static values = {
    entityType: String,
    relationType: String,
  }

  connect() {
    // HACK: This is a workaround for a flaw in Stimulus. Ideally, we would
    // connect to the `bulk-actions` controller as an outlet, but Stimulus was
    // having trouble finding the outlet when I tried that (I *think* because
    // the outlet is an ancestor of this controller's element).
    // This method works, but is not something we should have to do.
    this.bulkActionsController = stimulus.getControllerForElementAndIdentifier(
      this.element.closest('[data-controller="bulk-actions"]'),
      'bulk-actions',
    ) as BulkActionsController

    this.bulkActionsController.element.addEventListener(
      'bulk-actions:idsValue:changed',
      this.#handleEntityCheckboxChange.bind(this),
    )
  }

  disconnect() {
    this.bulkActionsController.element.removeEventListener(
      'bulk-actions:idsValue:changed',
      this.#handleEntityCheckboxChange.bind(this),
    )
  }

  relationCheckboxTargetConnected(element: HTMLInputElement) {
    element.addEventListener('input', this.#handleRelationCheckboxChange.bind(this))
  }

  relationCheckboxTargetDisconnected(element: HTMLInputElement) {
    element.removeEventListener('input', this.#handleRelationCheckboxChange.bind(this))
  }

  // Private methods

  /** Collapse all `<details>` elements */
  #closeDetails() {
    this.element.querySelectorAll('details').forEach(details => {
      details.removeAttribute('open')
    })
  }

  /**
   * Created a map of relation IDs to the number of times they appear in the
   * selected entities. This data is used to determine which relation checkboxes
   * are checked, indeterminate, marked as removed, or no-op'd.
   */
  #countRelations() {
    const entityCheckboxes = this.bulkActionsController.checkboxTargets
    const relationCounts: RelationCounts = {}

    entityCheckboxes
      .filter(checkbox => checkbox.checked)
      .forEach(entityCheckbox => {
        const entityRelations: EntityRelations = JSON.parse(entityCheckbox.dataset.entityRelations)

        entityRelations[this.relationTypeValue].forEach((relationId: RelationID) => {
          // If the relation already exists in the data, increment the count
          if (relationCounts[relationId]) {
            relationCounts[relationId] += 1
          }

          // If the relation doesn't exist, assign it and set the count to 1
          else {
            relationCounts[relationId] = 1
          }
        })
      })

    this.relationCounts = relationCounts
  }

  /** Utility method to create a `hiddenInput` target with a given name/value */
  #createHiddenInput(name: string, value: string) {
    const input = document.createElement('input')
    input.dataset.entityRelationsTarget = 'hiddenInput'
    input.name = name
    input.type = 'hidden'
    input.value = value
    return input
  }

  /** Respond whenever the user checks or unchecks an entity checkbox */
  #handleEntityCheckboxChange() {
    this.#resetHiddenInputs()
    this.#countRelations()
    this.#updateRelationCheckboxes()
    this.#updateRelationGroups()
    this.#closeDetails()
  }

  /** Determine what to do when the user checks a relation checkbox */
  #handleRelationCheckboxChange(event: Event) {
    const relationCheckbox = event.target as HTMLInputElement
    const { checked, value: relationId } = relationCheckbox
    const hiddenInput = this.hiddenInputTargets.find(input => input.value === relationId)
    const hiddenInputName = this.#hiddenInputName(checked ? 'add' : 'remove')
    const hasRelation = Object.keys(this.relationCounts).includes(relationId)

    // The presence of a hidden input indicates that the user has already
    // modified this relation's checkbox. If the relation is associated with
    // one or more of the entities that the user has checked, update the hidden
    // input's name to reflect whether we're adding or removing the relation.
    if (hiddenInput && hasRelation) {
      hiddenInput.name = hiddenInputName
    }

    // If a hidden input is present but the relation is NOT associated with any
    // of the checked entities, we can infer that it was previously created
    // as an "add" operation, but also that the user is trying to remove it now.
    else if (hiddenInput) {
      hiddenInput.remove()
    }

    // Otherwise, the user is applying a new relation
    // and we need to create a new hidden input for it.
    else {
      this.element.append(this.#createHiddenInput(hiddenInputName, relationId))
    }

    this.#setCheckboxRemovalState(relationCheckbox, hasRelation && !checked)
    this.#updateRelationGroups()
  }

  /** set the input name by data type values and an action (add or remove) */
  #hiddenInputName(action: string) {
    return `${this.entityTypeValue}[${this.relationTypeValue}_${action}][]`
  }

  /**
   * Deletes all of this controller's hidden inputs. This method is called
   * whenever the user checks or unchecks an entity checkbox.
   */
  #resetHiddenInputs() {
    this.hiddenInputTargets.forEach(input => {
      input.remove()
    })
  }

  /**
   *
   * @param relationCheckbox The relation checkbox to update
   * @param removed Set to `true` if the relation is currently associated with
   * one or more of the selected entities but the user has
   * unchecked the relation's checkbox.
   */
  #setCheckboxRemovalState(relationCheckbox: HTMLInputElement, removed: boolean) {
    relationCheckbox.dataset.removed = String(removed)
    relationCheckbox.parentElement.classList[removed ? 'add' : 'remove']('line-through', 'opacity-50')
  }

  /**
   * Update the state of the checkboxes based on the current state of the
   * selected entities. This method is called whenever the user checks
   * or unchecks an entity checkbox.
   */
  #updateRelationCheckboxes() {
    const entityCheckboxes = this.bulkActionsController.checkboxTargets
    const checkedEntityCount = entityCheckboxes.filter(checkbox => checkbox.checked).length

    this.relationCheckboxTargets.forEach(relationCheckbox => {
      const relationId = relationCheckbox.value
      const checked = Object.keys(this.relationCounts).includes(relationId)
      const indeterminate = checked && this.relationCounts[relationId] < checkedEntityCount

      relationCheckbox.checked = checked
      relationCheckbox.indeterminate = indeterminate

      this.#setCheckboxRemovalState(relationCheckbox, false)
    })
  }

  /**
   * Show or hide the indicators that communicate which relation groups
   * have one or more modified relation checkboxes within them. If groups are
   * not used in this controller instance, this method no-ops.
   */
  #updateRelationGroups() {
    this.groupIndicatorTargets.forEach(groupIndicator => {
      const groupCheckboxes = groupIndicator
        .closest('details')
        .querySelectorAll<HTMLInputElement>('input[data-entity-relations-target="relationCheckbox"]')

      let active = false

      for (const checkbox of groupCheckboxes) {
        if (checkbox.checked || checkbox.dataset.removed === 'true') {
          active = true
          break
        }
      }

      groupIndicator.classList.toggle('hidden', !active)
    })
  }
}
